/**
 * The second step of a sign-in (`/api/auth/2fa…`): a code from the
 * authenticator or a recovery code, or — where an organization requires
 * two-factor and this account has none — setting one up first. Nothing else
 * is reachable until it is done: the Worker holds no session for this browser
 * yet, only the short challenge in its `al_2fa` cookie.
 *
 * Verifying is the redesign's code field (§16): six digits in mono 20, sent on
 * the sixth, « Verify » reading « Verifying » while it runs, and « Use a
 * recovery code » under it for somebody without their phone.
 */
import { useCallback, useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Input, PrimaryButton } from '../../components/primitives';
import { api } from '../../lib/api';
import type { Me } from '../../lib/types';
import { TwoFactorSetup } from './TwoFactorSetup';
import { CodeInput, codeRefusal } from './two-factor-parts';

export function TwoFactorStep({ purpose, onSignedIn }: { purpose: 'verify' | 'enrol'; onSignedIn: (me: Me) => void }): React.JSX.Element {
  const [code, setCode] = useState('');
  // A recovery code instead of the app's six digits (13e): its own field, free text.
  const [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<Me | null>(null);
  const start = useCallback(() => api.post<{ secret: string; uri: string }>('/auth/2fa/setup'), []);
  const enable = useCallback(async (typed: string): Promise<string[]> => {
    const answer = await api.post<Me & { recoveryCodes: string[] }>('/auth/2fa/enable', { code: typed });
    setSignedIn(answer);
    return answer.recoveryCodes;
  }, []);

  if (purpose === 'enrol') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 14, color: color.inkSubtle }}>Your organization requires two-factor. Set it up to carry on.</p>
        <TwoFactorSetup start={start} enable={enable} onDone={() => signedIn && onSignedIn(signedIn)} />
      </div>
    );
  }

  const send = async (typed: string): Promise<void> => {
    if (busy || (!recovery && typed.length !== 6) || !typed.trim()) return;
    setError(null);
    setBusy(true);
    try {
      onSignedIn(await api.post<Me>('/auth/2fa', { code: typed.trim() }));
    } catch (failure) {
      setError(codeRefusal(failure, recovery ? 'recovery' : 'verify'));
      setBusy(false);
    }
  };
  const switchMode = (): void => {
    setRecovery(!recovery);
    setCode('');
    setError(null);
  };
  // Held until six digits are typed (13b); a recovery code has no validated reason yet, so it is not held.
  const held = !recovery && code.length < 6 ? 'Type the six digits first' : undefined;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void send(code);
      }}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <label htmlFor="tfa-code" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: color.ink, marginBottom: 6 }}>
        {recovery ? 'Recovery code' : 'Code'}
      </label>
      {recovery ? (
        <Input
          id="tfa-code"
          key="recovery"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={code}
          readOnly={busy}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={error ? 'tfa-error' : undefined}
          onChange={(event) => setCode(event.target.value)}
          style={{ height: 36, padding: '0 12px', fontFamily: font.mono, fontSize: 14.5, borderColor: error ? color.red : undefined }}
        />
      ) : (
        <CodeInput
          id="tfa-code"
          key="code"
          autoFocus
          value={code}
          onChange={setCode}
          onComplete={(typed) => void send(typed)}
          invalid={Boolean(error)}
          readOnly={busy}
          describedBy={error ? 'tfa-error' : undefined}
        />
      )}
      {error && (
        <p id="tfa-error" role="alert" style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.4, color: color.badText }}>
          {error}
        </p>
      )}
      <div style={{ height: 24 }} />
      <PrimaryButton type="submit" full size="lg" busy={busy} reason={busy ? undefined : held}>
        {busy ? 'Verifying' : 'Verify'}
      </PrimaryButton>
      <button
        type="button"
        onClick={switchMode}
        style={{
          alignSelf: 'flex-start',
          marginTop: 18,
          background: 'none',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 13.5,
          color: color.accentText
        }}
      >
        {recovery ? 'Use the authenticator app' : 'Use a recovery code'}
      </button>
    </form>
  );
}
